import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { MediaQueries } from '@hhgtech/hhg-components'
import {
  Button,
  LoadingOverlay,
  Select,
  SelectProps,
  Text,
} from '@hhgtech/hhg-components/mantine'
import {
  ActionIcon,
  Avatar,
  Container,
  ContainerProps,
  Flex,
  Group,
  SelectItem,
} from '@mantine/core'
import debounce from 'lodash/debounce'
import { useIntl } from 'react-intl'
import { shallow } from 'zustand/shallow'

import { callApiNew } from 'api/axios'
import { formatExpertUser } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { useIsMobile } from 'hooks/useIsMobile'
import { ExpertUserSummary } from 'interfaces/types'
import { Specialty } from 'interfaces/types/Specialty'
import { WithPagination } from 'interfaces/types/Utilities'
import { useExpertStore } from 'state/expert'
import { generateSelectItem } from 'utils/generateSelectItem'
import { trimVietnameseAccent } from 'utils/trimVietnameseAccent'

import { _NoResult } from './_noResult'
import { _NothingFound } from './_nothingFound'
import useSearch from './useSearch'

const _SpecialtySelectedItemComponent = forwardRef<
  HTMLDivElement,
  Specialty & SelectItem
>(function __SpecialtySelectedItemComponent(
  { label, countExperts, ...others },
  ref,
) {
  return (
    <div ref={ref} {...others}>
      <Text size="p4">
        <Text
          span
          sx={{
            display: 'inline',
            lineBreak: 'auto',
            whiteSpace: 'pre-line',
          }}
        >
          {label}
        </Text>
        <Text
          span
          color="gray.6"
          ml={4}
          sx={{
            display: 'inline',
          }}
        >
          ({countExperts})
        </Text>
      </Text>
    </div>
  )
})

const _ExpertSelectedItemComponent = forwardRef<
  HTMLDivElement,
  SelectItem & ExpertUserSummary
>(function __ExpertSelectedItemComponent(
  { label, avatarThumbnail, ...others },
  ref,
) {
  return (
    <div ref={ref} {...others}>
      <Group noWrap>
        <Avatar src={avatarThumbnail} radius={'xl'} />

        <div>
          <Text size="p4">{label}</Text>
        </div>
      </Group>
    </div>
  )
})

const SHARED_STYLES: SelectProps['styles'] = {
  nothingFound: {
    gridColumnStart: 1,
    gridColumnEnd: 3,
  },
  item: {
    wordBreak: 'break-word',
  },
}

type _SearchProps = {
  onSearch?: () => void
} & ContainerProps
export const _Search = (props: _SearchProps) => {
  const { onSearch, ...otherProps } = props
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()

  const { setExpertList } = useExpertStore((state) => state, shallow)

  const [specialtySearchValue, onSpecialtySearchValue] = useState('')
  const setSpecialtySearchValue = (s: string) => {
    onSpecialtySearchValue(s)
    if (!s) {
      handleSpecialtyChange(null)
    }
  }
  const [searchValue, setSearchValue] = useState('')
  const [suggestExperts, setSuggestExperts] = useState<
    (ExpertUserSummary & SelectItem)[]
  >([])
  const [isQuerying, setIsQuerying] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const prevCancelFn = useRef<() => void>(() => false)

  const {
    specialtyData,
    // expertData,
    selectedSpecialty,
    handleSpecialtyChange,
    handleExpertChange,
  } = useSearch()

  const searching = useCallback(async () => {
    const tempSelectedSpecialty = specialtyData.find(
      (specialty) => specialty.termId === selectedSpecialty?.termId,
    )
    const response = await callApiNew(
      PATHS.EXPERT.GET_EXPERT_BY_NAME_AND_SPECIALTY,
      {
        params: {
          // page,
          specialty: tempSelectedSpecialty?.termId || '',
          ...(searchValue !== ''
            ? {
                search_by: 'display_name',
                name: searchValue,
              }
            : {}),
        },
      },
    )
    return {
      experts: response?.data?.experts.map(
        formatExpertUser,
      ) as Array<ExpertUserSummary>,
      pagination: response?.data?.paging,
    } as WithPagination<{ experts: Array<ExpertUserSummary> }>
  }, [selectedSpecialty, searchValue])

  // NEW: cancellable debounce query function
  const querySuggestExperts = useCallback(
    debounce(async (search: string, specialtyId: string) => {
      setIsQuerying(true)
      let cancel = false
      prevCancelFn.current = () => {
        cancel = true
      }
      try {
        const response = await callApiNew(
          PATHS.EXPERT.GET_EXPERT_BY_NAME_AND_SPECIALTY,
          {
            params: {
              specialty: specialtyId || '',
              search_by: 'display_name',
              name: search,
            },
          },
        )
        if (cancel) return
        setSuggestExperts(
          generateSelectItem(
            (response?.data?.experts.map(formatExpertUser) ||
              []) as Array<ExpertUserSummary>,
            'displayName',
            'displayName',
          ),
        )
      } catch (e) {
        console.error(e)
      }
      setIsQuerying(false)
    }, 200),
    [],
  )

  const handleSearch = async () => {
    setIsSearching(true)
    const expert = await searching()
    setExpertList(expert)
    setIsSearching(false)
    onSearch?.()
  }
  const onClearExpert = async () => {
    setSearchValue('')
  }

  useEffect(() => {
    querySuggestExperts(searchValue, String(selectedSpecialty?.termId || ''))
    return () => {
      prevCancelFn.current?.()
    }
  }, [searchValue, selectedSpecialty?.termId])

  // need memo to prevent rerendering, however can't separate because need data on isQuerying
  const CustomDropDown = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      ({ children }: { children?: ReactNode }) => {
        return (
          <div
            style={{
              overflow: 'auto',
              width: '100%',
            }}
          >
            {children}
            <LoadingOverlay visible={isQuerying} />
          </div>
        )
      },
    [isQuerying],
  )

  return (
    <Container
      size="lg"
      py={{ base: 8, md: 24 }}
      px={{ base: 0, md: 16 }}
      {...otherProps}
    >
      <Flex
        w="100%"
        justify={'space-between'}
        gap={{ base: 16, md: 24 }}
        direction={{ base: 'column', md: 'row' }}
      >
        <Select
          {...(specialtySearchValue.length > 0
            ? {
                rightSection: (
                  <ActionIcon
                    onClick={() => {
                      setSpecialtySearchValue('')
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M1.6665 9.99996C1.6665 5.39163 5.3915 1.66663 9.99984 1.66663C14.6082 1.66663 18.3332 5.39163 18.3332 9.99996C18.3332 14.6083 14.6082 18.3333 9.99984 18.3333C5.3915 18.3333 1.6665 14.6083 1.6665 9.99996ZM12.4083 13.5831C12.7333 13.9081 13.2583 13.9081 13.5833 13.5831C13.9 13.2581 13.9 12.7247 13.5833 12.4081L11.175 9.99973L13.5833 7.5914C13.9083 7.2664 13.9083 6.7414 13.5833 6.4164C13.2583 6.0914 12.7333 6.0914 12.4083 6.4164L10 8.82473L7.59167 6.4164C7.26667 6.0914 6.74167 6.0914 6.41667 6.4164C6.26063 6.57209 6.17293 6.78347 6.17293 7.0039C6.17293 7.22433 6.26063 7.43571 6.41667 7.5914L8.825 9.99973L6.41667 12.4081C6.26063 12.5638 6.17293 12.7751 6.17293 12.9956C6.17293 13.216 6.26063 13.4274 6.41667 13.5831C6.74167 13.9081 7.26667 13.9081 7.59167 13.5831L10 11.1747L12.4083 13.5831Z"
                        fill="#8C8C8C"
                      />
                    </svg>
                  </ActionIcon>
                ),
              }
            : {})}
          allowDeselect
          data={specialtyData}
          dropdownPosition="bottom"
          itemComponent={_SpecialtySelectedItemComponent}
          keepSearch
          limit={100}
          nothingFound={<_NothingFound />}
          onChange={setSpecialtySearchValue}
          value={specialtySearchValue}
          onItemSubmit={(item: any) => handleSpecialtyChange(item)}
          searchable
          size="lg"
          w={isMobile ? '100%' : '30%'}
          placeholder={f({
            id: 'expertPage.search.specialty.placeholder',
            defaultMessage: 'Choose a specialty',
          })}
          label={
            isMobile && (
              <Text size="label2" weight="bold">
                {f({
                  id: 'expert.search.specialty',
                  defaultMessage: 'Specialty',
                })}
              </Text>
            )
          }
          styles={SHARED_STYLES}
          filter={(value: string, item: SelectItem & Specialty) =>
            trimVietnameseAccent(item?.label?.toLowerCase()).includes(
              trimVietnameseAccent(value.toLowerCase()).trim(),
            ) ||
            trimVietnameseAccent(item?.name?.toLowerCase()).includes(
              trimVietnameseAccent(value.toLowerCase()).trim(),
            )
          }
        />
        <Select
          {...(searchValue.length > 0
            ? {
                rightSection: (
                  <ActionIcon
                    onClick={onClearExpert}
                    sx={{ cursor: 'pointer' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M1.6665 9.99996C1.6665 5.39163 5.3915 1.66663 9.99984 1.66663C14.6082 1.66663 18.3332 5.39163 18.3332 9.99996C18.3332 14.6083 14.6082 18.3333 9.99984 18.3333C5.3915 18.3333 1.6665 14.6083 1.6665 9.99996ZM12.4083 13.5831C12.7333 13.9081 13.2583 13.9081 13.5833 13.5831C13.9 13.2581 13.9 12.7247 13.5833 12.4081L11.175 9.99973L13.5833 7.5914C13.9083 7.2664 13.9083 6.7414 13.5833 6.4164C13.2583 6.0914 12.7333 6.0914 12.4083 6.4164L10 8.82473L7.59167 6.4164C7.26667 6.0914 6.74167 6.0914 6.41667 6.4164C6.26063 6.57209 6.17293 6.78347 6.17293 7.0039C6.17293 7.22433 6.26063 7.43571 6.41667 7.5914L8.825 9.99973L6.41667 12.4081C6.26063 12.5638 6.17293 12.7751 6.17293 12.9956C6.17293 13.216 6.26063 13.4274 6.41667 13.5831C6.74167 13.9081 7.26667 13.9081 7.59167 13.5831L10 11.1747L12.4083 13.5831Z"
                        fill="#8C8C8C"
                      />
                    </svg>
                  </ActionIcon>
                ),
              }
            : {})}
          allowDeselect
          data={suggestExperts}
          dropdownPosition="bottom"
          itemComponent={_ExpertSelectedItemComponent}
          keepSearch
          limit={100}
          dropdownComponent={CustomDropDown}
          nothingFound={<_NoResult py={40} />}
          onChange={setSearchValue}
          value={searchValue}
          onItemSubmit={handleExpertChange}
          searchable
          size="lg"
          sx={{ flexGrow: 1 }}
          placeholder={f({
            id: 'expertPage.search.expert.placeholder',
            defaultMessage: "Search by expert's name",
          })}
          label={
            isMobile && (
              <Text size="label2" weight="bold">
                {f({ id: 'careBooking.doctor', defaultMessage: 'Doctor' })}
              </Text>
            )
          }
          styles={{
            ...SHARED_STYLES,
            itemsWrapper: {
              [MediaQueries.mbUp]: {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 4,
              },
            },
          }}
          filter={() => true}
        />
        <Button size="lg" onClick={handleSearch} loading={isSearching}>
          {f({
            id: 'navigation.search.input.placeholder',
            defaultMessage: 'Search',
          })}
        </Button>
      </Flex>
    </Container>
  )
}
